import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon, type IconName } from './Icon.tsx';

/**
 * ⌘K: go anywhere by typing its name.
 *
 * The rail is ten destinations, which is the right number to scan and the
 * wrong number to hold everything — a workflow, a factory, a connection and
 * a section of Insights are all places a person means when they say "take
 * me to…", and none of them fits on a rail. The palette is where they go
 * instead, and it is fed by the shell rather than fetching anything: it opens
 * in the same frame as the keystroke.
 *
 * Keyboard-first by design. Arrow keys move, Enter goes, Escape closes, and
 * the list is a `listbox` so a screen reader announces the same thing the
 * highlight shows.
 */
export interface Command {
  id: string;
  label: string;
  /** Where it lives — "Workflow", "Factory", "Insights" — shown beside the label. */
  group: string;
  icon: IconName;
  keywords?: string;
  /**
   * What tells two hits apart — a run's workflow and status, a work item's
   * state. Only the hits the Worker finds carry one; a screen needs none.
   */
  detail?: string;
  run(): void;
}

/**
 * How much has to be typed before the Worker is asked.
 *
 * Under three characters the static list is the better answer and the remote
 * one is noise: every run id starts with `run`, so a two-character query would
 * fetch the ten newest runs on every open. Three is where a query starts to
 * mean something specific.
 */
const REMOTE_MIN_LENGTH = 3;

/**
 * How long typing has to pause before a request goes out. Short enough that a
 * paste answers at once, long enough that typing a title does not fire a
 * request per keystroke.
 */
const REMOTE_DEBOUNCE_MS = 150;

export function CommandPalette({
  open,
  commands,
  search,
  onClose,
}: {
  open: boolean;
  commands: readonly Command[];
  /**
   * What the Worker can find that the shell does not hold — runs, work items,
   * workflows by name. Optional, because the palette must still open in the
   * same frame as the key with nothing behind it; the static list is the
   * palette, and this appends to it.
   */
  search?: (query: string) => Promise<Command[]>;
  onClose(): void;
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  /*
   * The hits the Worker answered with, appended under the static results. Held
   * apart from `shown` rather than merged into the ranking, because the static
   * results are already on screen with the cursor on one of them: a remote
   * answer that re-sorted the list would move the row under Enter.
   */
  const [remote, setRemote] = useState<Command[]>([]);
  const [searching, setSearching] = useState(false);
  /*
   * Which request is the one whose answer still matters. Requests are issued
   * per pause in typing and answer in any order; an answer for an earlier,
   * shorter query arriving after the current one's would install the wrong
   * hits under the right query, and nothing on screen would say so.
   */
  const ticket = useRef(0);

  useEffect(() => {
    const needle = query.trim();
    ticket.current += 1;
    const mine = ticket.current;
    // Cleared at once rather than when the answer lands, so a query that
    // changed never shows the previous query's hits as if they were its own.
    setRemote([]);
    if (!open || !search || needle.length < REMOTE_MIN_LENGTH) {
      setSearching(false);
      return;
    }
    const timer = setTimeout(() => {
      setSearching(true);
      search(needle).then(
        (hits) => {
          if (mine !== ticket.current) return;
          setRemote(hits);
          setSearching(false);
        },
        () => {
          // A failed search is an empty one. The static results are still
          // there to pick from, and a banner inside a palette is in the way
          // of the thing the person was about to do.
          if (mine === ticket.current) setSearching(false);
        },
      );
    }, REMOTE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, open, search]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    // Focus after the dialog has painted; focusing an element that is about to
    // mount focuses nothing.
    const frame = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  /*
   * Ranked, not merely filtered. A name that starts with what was typed
   * outranks one that contains it, and both outrank a hit in the keywords —
   * typing "polic" must put Policies first, not the Settings screen whose
   * hint happens to mention policies. Without the ranking the screens, which
   * are listed first, won every tie, and Enter went to the wrong place.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return commands.slice(0, 12);
    const ranked = commands
      .map((command) => {
        const label = command.label.toLowerCase();
        const rank = label.startsWith(needle)
          ? 0
          : label.includes(needle)
            ? 1
            : `${command.group} ${command.keywords ?? ''}`.toLowerCase().includes(needle)
              ? 2
              : null;
        return rank === null ? null : { command, rank };
      })
      .filter((entry): entry is { command: Command; rank: number } => entry !== null)
      .sort((a, b) => a.rank - b.rank);
    return ranked.slice(0, 12).map((entry) => entry.command);
  }, [commands, query]);

  /*
   * Reset on the *static* results only. A remote answer appends below them, so
   * the row under the cursor is still the row under the cursor when it lands;
   * resetting on the appended list would yank the highlight back to the top
   * the moment the Worker answered, a beat after the person had arrowed down.
   */
  useEffect(() => setActive(0), [shown]);

  /*
   * One list for the keyboard, two sources. A workflow the shell already
   * matched by name is not listed twice when the Worker finds it too — the
   * static entry is the one the cursor may already be on, so it is the one
   * that stays.
   */
  const appended = useMemo(
    () => remote.filter((hit) => !shown.some((command) => command.id === hit.id)),
    [remote, shown],
  );
  const all = appended.length ? [...shown, ...appended] : shown;

  if (!open) return null;

  const choose = (command: Command | undefined) => {
    if (!command) return;
    onClose();
    command.run();
  };

  return (
    <div className="palette-backdrop" onMouseDown={onClose}>
      <div
        className="palette"
        role="dialog"
        aria-label="Go to"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="palette-input">
          <Icon name="search" />
          <input
            ref={input}
            value={query}
            placeholder="Go to a screen, a factory, a workflow, a connection…"
            aria-label="Go to"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActive((index) => Math.min(index + 1, all.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActive((index) => Math.max(index - 1, 0));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                choose(all[active]);
              } else if (event.key === 'Escape') {
                onClose();
              }
            }}
          />
          <kbd>esc</kbd>
        </div>
        <ul className="palette-list" role="listbox" aria-label="Destinations">
          {all.length === 0 && !searching && <li className="palette-empty">Nothing matches.</li>}
          {all.map((command, index) => (
            <li key={command.id} role="option" aria-selected={index === active}>
              <button
                type="button"
                className={`palette-item ${index === active ? 'active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(command)}
              >
                <Icon name={command.icon} />
                <span className="palette-label">{command.label}</span>
                {command.detail && <span className="palette-detail">{command.detail}</span>}
                <span className="palette-group">{command.group}</span>
              </button>
            </li>
          ))}
          {searching && (
            // Below the static results rather than in place of them: they are
            // already there to pick from, and this only says more may come.
            <li className="palette-searching" aria-live="polite">
              searching…
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
